import type { ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-5">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-heading)' }}>{title}</h1>
        {subtitle && <p className="text-sm mt-0.5" style={{ color: 'var(--text-faint)' }}>{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function KpiCard({ label, value, delta, deltaTone, hint }: { label: string; value: string; delta?: string; deltaTone?: 'up' | 'down' | 'neutral' | 'good' | 'bad'; hint?: string }) {
  const toneColor = deltaTone === 'up' || deltaTone === 'good' ? 'var(--success)' : deltaTone === 'down' || deltaTone === 'bad' ? 'var(--danger)' : 'var(--text-faint)';
  return (
    <div className="card p-4">
      <div className="text-xs" style={{ color: 'var(--text-faint)' }}>{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: 'var(--text-heading)' }}>{value}</div>
      {(delta || hint) && (
        <div className="text-xs mt-1" style={{ color: toneColor }}>{delta ?? hint}</div>
      )}
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'success' | 'warn' | 'danger' | 'neutral'; children: ReactNode }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="card flex flex-col items-center justify-center py-16 text-center">
      <div className="font-semibold" style={{ color: 'var(--text-heading)' }}>{title}</div>
      {body && <div className="text-sm mt-1 max-w-sm" style={{ color: 'var(--text-faint)' }}>{body}</div>}
    </div>
  );
}

export function Loading() {
  return <div className="py-16 text-center text-sm" style={{ color: 'var(--text-faint)' }}>Loading…</div>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="card p-4 text-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger-bg)' }}>
      {message}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,.4)' }} onClick={onClose}>
      <div className="card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: 'var(--text-heading)' }}>{title}</h3>
          <button onClick={onClose} style={{ color: 'var(--text-faint)' }}>✕</button>
        </div>
        {children}
        {footer && <div className="flex justify-end gap-2 mt-4">{footer}</div>}
      </div>
    </div>
  );
}

/** A labeled on/off switch (track + knob), styled with the brand accent when on. */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer" style={{ opacity: disabled ? 0.5 : 1 }}>
      {label && <span className="text-sm" style={{ color: 'var(--text-body)' }}>{label}</span>}
      <span
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        style={{
          width: 36,
          height: 20,
          borderRadius: 999,
          background: checked ? 'var(--brand)' : 'var(--border-strong)',
          position: 'relative',
          transition: 'background .15s',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,.25)',
            transition: 'left .15s',
          }}
        />
      </span>
    </label>
  );
}

/** A single-row multi-select pill group, e.g. the Platforms page's category toggles. */
export function PillToggleGroup({ options, value, onToggle }: { options: { key: string; label: string }[]; value: string[]; onToggle: (key: string) => void }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {options.map((o) => {
        const active = value.includes(o.key);
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onToggle(o.key)}
            className="text-xs font-medium"
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
              background: active ? 'var(--warn-bg-soft)' : 'var(--surface-muted)',
              color: active ? 'var(--brand-dark)' : 'var(--text-muted)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A labeled 0-100 range slider showing its current value. */
export function PercentSlider({ value, onChange, max = 30, label }: { value: number; onChange: (v: number) => void; max?: number; label?: string }) {
  return (
    <div>
      {label && (
        <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
          <span>{label}</span>
          <span className="font-semibold" style={{ color: 'var(--text-heading)' }}>{value}%</span>
        </div>
      )}
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--brand)' }}
      />
    </div>
  );
}

